import { ChevronLeft, ChevronRight, Eye, EyeOff, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type {
  Flashcard,
  FlashcardOptionsResponse,
  LessonFlashcardsResponse,
} from "@learn-flow/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteFlashcard,
  flashcardOptionsKey,
  lessonFlashcardsKey,
  loadLessonFlashcards,
} from "./flashcard-api.js";
import { shuffledCopy } from "../flashcards/flashcards-preview-model.js";

export function LessonFlashcardsPanel({
  activityId,
  active,
}: {
  activityId: string;
  active: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const cardsQuery = useQuery({
    queryKey: lessonFlashcardsKey(activityId),
    queryFn: () => loadLessonFlashcards(activityId),
    enabled: active,
    retry: false,
  });
  const [revealedCardId, setRevealedCardId] = useState<string | null>(null);
  const [reviewCards, setReviewCards] = useState<Flashcard[]>([]);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const [reviewAnswerVisible, setReviewAnswerVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    card: Flashcard;
    trigger: HTMLButtonElement;
  } | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const deletePendingRef = useRef(false);
  const startReviewRef = useRef<HTMLButtonElement>(null);
  const reviewQuestionRef = useRef<HTMLHeadingElement>(null);
  const cards = cardsQuery.data?.flashcards ?? [];
  const activeCards = cards.filter((card) => card.archivedAt === null);
  const deleteMutation = useMutation({
    mutationFn: (cardId: string) => deleteFlashcard(activityId, cardId),
    onSuccess: (result) => {
      queryClient.setQueryData<LessonFlashcardsResponse>(
        lessonFlashcardsKey(activityId),
        (current) =>
          current
            ? {
                flashcards: current.flashcards.filter(
                  (card) => card.id !== result.deletedFlashcardId,
                ),
              }
            : current,
      );
      queryClient.setQueryData<FlashcardOptionsResponse>(
        flashcardOptionsKey(activityId),
        (current) => (current ? { ...current, flashcardCount: result.flashcardCount } : current),
      );
      setRevealedCardId((current) => (current === result.deletedFlashcardId ? null : current));
      setDeleteTarget(null);
    },
  });

  useEffect(() => {
    if (!active || reviewIndex === null) return;
    reviewQuestionRef.current?.focus();
  }, [active, reviewIndex]);

  useEffect(() => {
    if (reviewIndex !== null && reviewIndex >= reviewCards.length) {
      setReviewIndex(null);
      setReviewAnswerVisible(false);
    }
  }, [reviewCards.length, reviewIndex]);

  const finishReview = () => {
    setReviewIndex(null);
    setReviewCards([]);
    setReviewAnswerVisible(false);
    window.requestAnimationFrame(() => startReviewRef.current?.focus());
  };

  if (cardsQuery.isPending) {
    return (
      <div className="lesson-flashcards-state" aria-busy="true" aria-label="Loading flashcards">
        <span className="section-kicker">Lesson flashcards</span>
        <p>Loading your flashcards…</p>
      </div>
    );
  }

  if (cardsQuery.isError) {
    return (
      <div className="lesson-flashcards-state" role="alert">
        <span className="section-kicker">Lesson flashcards</span>
        <h3>Flashcards could not be loaded</h3>
        <p>Your cards are still saved. Try loading them again.</p>
        <button type="button" className="soft-button" onClick={() => void cardsQuery.refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (reviewIndex !== null) {
    const card = reviewCards[reviewIndex];
    if (!card) return <div />;
    const isLastCard = reviewIndex === reviewCards.length - 1;
    return (
      <section className="flashcard-review" aria-labelledby="flashcard-review-question">
        <div className="flashcard-review-heading">
          <div>
            <span className="section-kicker">Reviewing this lesson</span>
            <p aria-live="polite">
              Card {reviewIndex + 1} of {reviewCards.length}
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Exit review"
            onClick={finishReview}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <article className="flashcard-review-card">
          <span className="flashcard-side-label">Question</span>
          <h3 id="flashcard-review-question" ref={reviewQuestionRef} tabIndex={-1}>
            {card.question}
          </h3>
          {reviewAnswerVisible ? (
            <div className="flashcard-review-answer">
              <span className="flashcard-side-label">Answer</span>
              <p>{card.answer}</p>
              {card.notes ? (
                <div className="lesson-flashcard-notes">
                  <span className="flashcard-side-label">Notes</span>
                  <p>{card.notes}</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="soft-button flashcard-reveal-button"
            aria-expanded={reviewAnswerVisible}
            onClick={() => setReviewAnswerVisible((visible) => !visible)}
          >
            {reviewAnswerVisible ? (
              <>
                <EyeOff size={16} aria-hidden="true" /> Hide answer
              </>
            ) : (
              <>
                <Eye size={16} aria-hidden="true" /> Reveal answer
              </>
            )}
          </button>
        </article>
        <div className="flashcard-review-navigation">
          <button
            type="button"
            className="soft-button"
            disabled={reviewIndex === 0}
            onClick={() => {
              setReviewAnswerVisible(false);
              setReviewIndex((index) => Math.max(0, (index ?? 0) - 1));
            }}
          >
            <ChevronLeft size={16} aria-hidden="true" /> Previous
          </button>
          {isLastCard ? (
            <button type="button" className="primary-button" onClick={finishReview}>
              Finish review
            </button>
          ) : (
            <button
              type="button"
              className="primary-button"
              onClick={() => {
                setReviewAnswerVisible(false);
                setReviewIndex((index) => Math.min(reviewCards.length - 1, (index ?? 0) + 1));
              }}
            >
              Next <ChevronRight size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </section>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="lesson-flashcards-state lesson-flashcards-empty">
        <span className="section-kicker">Lesson flashcards</span>
        <h3>No flashcards yet</h3>
        <p>Create one from the button above or select text from your notes.</p>
      </div>
    );
  }

  return (
    <section className="lesson-flashcards-list-view" aria-label="Lesson flashcards">
      <div className="lesson-flashcards-list-heading">
        <div>
          <span className="section-kicker">Lesson flashcards</span>
          <h3>Questions to revisit</h3>
        </div>
        <span>{cards.length} total</span>
      </div>
      <ol className="lesson-flashcards-list">
        {cards.map((card) => (
          <FlashcardListItem
            key={card.id}
            card={card}
            archived={card.archivedAt !== null}
            revealed={revealedCardId === card.id}
            onToggle={() => setRevealedCardId((current) => (current === card.id ? null : card.id))}
            onDelete={(trigger) => {
              deleteMutation.reset();
              deletePendingRef.current = false;
              setDeletePending(false);
              setDeleteTarget({ card, trigger });
            }}
          />
        ))}
      </ol>
      <button
        ref={startReviewRef}
        type="button"
        className="primary-button lesson-flashcards-start"
        disabled={activeCards.length === 0}
        onClick={() => {
          setReviewAnswerVisible(false);
          setReviewCards(shuffledCopy(activeCards));
          setReviewIndex(0);
        }}
      >
        {activeCards.length === 0 ? "No active cards to review" : "Start review"}
      </button>
      {deleteTarget ? (
        <DeleteFlashcardDialog
          card={deleteTarget.card}
          pending={deletePending}
          failed={deleteMutation.isError}
          onConfirm={() => {
            if (deletePendingRef.current) return;
            deletePendingRef.current = true;
            setDeletePending(true);
            deleteMutation.mutate(deleteTarget.card.id, {
              onSettled: () => {
                deletePendingRef.current = false;
                setDeletePending(false);
              },
            });
          }}
          onDismiss={() => {
            if (deletePendingRef.current) return;
            const trigger = deleteTarget.trigger;
            deleteMutation.reset();
            setDeleteTarget(null);
            window.requestAnimationFrame(() => trigger.focus());
          }}
        />
      ) : null}
    </section>
  );
}

function FlashcardListItem({
  card,
  archived,
  revealed,
  onToggle,
  onDelete,
}: {
  card: Flashcard;
  archived: boolean;
  revealed: boolean;
  onToggle: () => void;
  onDelete: (trigger: HTMLButtonElement) => void;
}): React.JSX.Element {
  return (
    <li className="lesson-flashcard-item">
      <div className="lesson-flashcard-content">
        <h4>{card.question}</h4>
        {archived ? <span className="flashcard-archived-label">Archived</span> : null}
        <button type="button" className="text-button" aria-expanded={revealed} onClick={onToggle}>
          {revealed ? (
            <>
              <EyeOff size={14} aria-hidden="true" /> Hide answer
            </>
          ) : (
            <>
              <Eye size={14} aria-hidden="true" /> Reveal answer
            </>
          )}
        </button>
        {revealed ? (
          <div className="lesson-flashcard-answer">
            <span className="flashcard-side-label">Answer</span>
            <p>{card.answer}</p>
            {card.notes ? (
              <div className="lesson-flashcard-notes">
                <span className="flashcard-side-label">Notes</span>
                <p>{card.notes}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="flashcard-delete-button"
        onClick={(event) => onDelete(event.currentTarget)}
      >
        <Trash2 size={14} aria-hidden="true" /> Delete
      </button>
    </li>
  );
}

function DeleteFlashcardDialog({
  card,
  pending,
  failed,
  onConfirm,
  onDismiss,
}: {
  card: Flashcard;
  pending: boolean;
  failed: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    keepButtonRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="calm-dialog delete-flashcard-dialog"
      aria-labelledby="delete-flashcard-title"
      aria-describedby="delete-flashcard-description"
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onDismiss();
      }}
    >
      <div>
        <span className="section-kicker">Delete flashcard</span>
        <h2 id="delete-flashcard-title">Delete this flashcard?</h2>
        <p className="delete-flashcard-question">{card.question}</p>
        <p id="delete-flashcard-description">
          This flashcard will be permanently deleted. This cannot be undone.
        </p>
        {failed ? (
          <p className="field-error" role="alert">
            The flashcard could not be deleted. It is still in this lesson; try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            ref={keepButtonRef}
            type="button"
            className="soft-button"
            disabled={pending}
            onClick={onDismiss}
          >
            Keep flashcard
          </button>
          <button
            type="button"
            className="danger-button"
            aria-label="Delete flashcard"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? "Deleting…" : "Delete flashcard"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
